import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Camera, MapPin, Calendar, Heart, Clock, Upload, X, CheckCircle,
  Loader2, User, ImageIcon, Download, Shield, Aperture
} from 'lucide-react';
import axios from 'axios';
import GalleryDonateTab from '../components/GalleryDonateTab';
import FloatingDonateTab from '../components/FloatingDonateTab';
import { compressImage } from '../lib/mediaUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Public gallery upload accepts anything a modern phone camera roll can
// produce. We compress in the browser (canvas → WebP@0.85, max 2000px)
// via the shared helper so a 12 MB iPhone shot lands at ~500 KB before
// the network hop. Formats the canvas can't decode (mostly HEIC on
// non-Safari) pass through raw and the backend accepts up to 20 MB.
const ACCEPTED_EXT = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
const RAW_ACCEPT_MB = 25;
const BACKEND_HARD_MB = 20;

const CONSENT_TEXT = "By uploading this photo, I confirm that I have permission from anyone shown in the image and grant Novena Humanitarian Foundation the right to use, publish, and share this image on its website, social media, and promotional materials, now and in the future.";

/* ===== Photo Grid with Lightbox ===== */
const PhotoGrid = ({ photos }) => {
  const [lightbox, setLightbox] = useState(null);
  if (!photos.length) return null;

  return (
    <>
      <div className="grid grid-cols-3 gap-1.5" data-testid="gallery-photo-grid">
        {photos.map((p, i) => (
          <button key={p.id} onClick={() => setLightbox(i)}
            className="aspect-square rounded-lg overflow-hidden bg-[#F1F5F9] relative group" data-testid={`gallery-photo-${p.id}`}>
            <img src={p.image_url} alt={p.guest_name || 'Event photo'} className="w-full h-full object-cover" loading="lazy" />
            {p.guest_name && (
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-[10px] text-white truncate">{p.guest_name}</p>
              </div>
            )}
          </button>
        ))}
      </div>
      {lightbox !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white/70 hover:text-white z-10"><X className="w-6 h-6" /></button>
          <img src={photos[lightbox].image_url} alt="" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
          <div className="absolute bottom-4 flex items-center gap-4">
            <button onClick={(e) => { e.stopPropagation(); setLightbox(Math.max(0, lightbox - 1)); }}
              disabled={lightbox === 0} className="text-white/40 hover:text-white disabled:opacity-20 text-3xl px-2">&#8249;</button>
            <span className="text-white/50 text-xs">{lightbox + 1} / {photos.length}</span>
            <button onClick={(e) => { e.stopPropagation(); setLightbox(Math.min(photos.length - 1, lightbox + 1)); }}
              disabled={lightbox === photos.length - 1} className="text-white/40 hover:text-white disabled:opacity-20 text-3xl px-2">&#8250;</button>
          </div>
        </div>
      )}
    </>
  );
};

/* ===== Preview Screen ===== */
const PhotoPreview = ({ items, onUpload, onSave, onRemove, onAddMore, guestName, setGuestName, consentChecked, setConsentChecked, uploading, uploadProgress }) => {

  const handleSave = (item) => {
    const link = document.createElement('a');
    link.href = item.preview;
    link.download = item.file.name || 'photo.jpg';
    link.click();
  };

  if (uploading) {
    return (
      <div className="text-center py-8" data-testid="upload-progress">
        <Loader2 className="w-10 h-10 animate-spin text-[#BA1826] mx-auto mb-3" />
        <p className="text-sm font-semibold text-[#0A0A0A]">Uploading {uploadProgress.done} of {uploadProgress.total}...</p>
        <div className="w-full bg-[#E5E7EB] rounded-full h-2.5 mt-3">
          <div className="bg-[#BA1826] h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="photo-preview-screen">
      {/* Preview Grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {items.map((item, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-[#F1F5F9]">
            <img src={item.preview} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 flex flex-col items-center justify-center gap-1.5 opacity-0 hover:opacity-100 hover:bg-black/40 transition-all">
              <button onClick={() => handleSave(item)}
                className="w-7 h-7 bg-white rounded-full flex items-center justify-center shadow" title="Save to phone">
                <Download className="w-3.5 h-3.5 text-[#0A0A0A]" />
              </button>
              <button onClick={() => onRemove(i)}
                className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center shadow" title="Remove">
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          </div>
        ))}
        <button onClick={onAddMore}
          className="aspect-square rounded-lg border-2 border-dashed border-[#D1D5DB] flex flex-col items-center justify-center hover:border-[#BA1826]/40 transition-colors"
          data-testid="add-more-btn">
          <Camera className="w-5 h-5 text-[#9CA3AF]" />
          <span className="text-[9px] text-[#9CA3AF] mt-1">Add more</span>
        </button>
      </div>

      {/* Guest Name */}
      <div className="mb-4">
        <label className="flex items-center gap-1.5 text-xs font-medium text-[#4B5563] mb-1.5">
          <User className="w-3 h-3" /> Your Name <span className="text-[#9CA3AF]">(optional)</span>
        </label>
        <input type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)}
          placeholder="e.g., Sarah M." maxLength={100}
          className="w-full px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
          data-testid="guest-name-input" />
      </div>

      {/* Consent Checkbox */}
      <label className="flex items-start gap-2.5 p-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg mb-4 cursor-pointer" data-testid="consent-checkbox-label">
        <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)}
          className="accent-[#BA1826] mt-0.5 flex-shrink-0" data-testid="consent-checkbox" />
        <span className="text-[11px] text-[#4B5563] leading-relaxed">{CONSENT_TEXT}</span>
      </label>

      {/* Action Buttons */}
      <div className="space-y-2">
        <button onClick={onUpload} disabled={!consentChecked}
          className="w-full py-3 bg-[#BA1826] text-white font-semibold text-sm rounded-lg hover:bg-[#A41623] transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="upload-to-album-btn">
          <Upload className="w-4 h-4" />
          Upload {items.length} Photo{items.length !== 1 ? 's' : ''} to Event Album
        </button>
        {items.length === 1 && (
          <button onClick={() => handleSave(items[0])}
            className="w-full py-2.5 border border-[#E5E7EB] text-[#4B5563] font-semibold text-sm rounded-lg hover:bg-[#F8F9FA] transition-colors flex items-center justify-center gap-2"
            data-testid="save-to-phone-btn">
            <Download className="w-4 h-4" />
            Save to My Phone
          </button>
        )}
      </div>
    </div>
  );
};

/* ===== Main Page ===== */
export default function GalleryUploadPage() {
  const { galleryId } = useParams();
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [previewItems, setPreviewItems] = useState([]);
  const [guestName, setGuestName] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [uploadResults, setUploadResults] = useState([]);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchGallery = useCallback(async () => {
    try {
      const [galRes, photosRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/galleries/${galleryId}`),
        axios.get(`${BACKEND_URL}/api/public/galleries/${galleryId}/photos`).catch(() => ({ data: [] })),
      ]);
      setGallery(galRes.data);
      setPhotos(photosRes.data);
    } catch (e) {
      setError(e.response?.status === 404 ? 'not_found' : 'server_error');
    }
    setLoading(false);
  }, [galleryId]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    // Step 1: extension whitelist + a soft cap so we don't try to
    // decode a 200 MB raw file into a canvas.
    const skippedExt = [];
    const skippedSize = [];
    const kept = [];
    for (const f of files) {
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (!ACCEPTED_EXT.includes(ext)) { skippedExt.push(f.name); continue; }
      if (f.size > RAW_ACCEPT_MB * 1024 * 1024) { skippedSize.push(f.name); continue; }
      kept.push(f);
    }

    // Step 2: shared compression. Files under 2 MB pass through
    // untouched; larger ones are recoded to WebP@0.85 max-2000px so
    // upload is fast on phones. Compression failures (HEIC on
    // non-Safari) fall through to the raw file.
    setUploading(true);   // reuse the flag so the button shows a spinner
    const processed = [];
    const oversizeAfter = [];
    for (const f of kept) {
      let out;
      try {
        out = await compressImage(f);
        if (!out || !out.size) out = f;
      } catch (_) {
        out = f;
      }
      // Backend safety cap — anything still >20 MB after processing
      // gets rejected client-side so we don't waste the round-trip.
      if (out.size > BACKEND_HARD_MB * 1024 * 1024) {
        oversizeAfter.push(f.name);
        continue;
      }
      processed.push({ file: out, preview: URL.createObjectURL(out) });
    }
    setUploading(false);

    // Step 3: append + tell the user about anything we skipped.
    setPreviewItems(prev => [...prev, ...processed]);
    const msgs = [];
    if (skippedExt.length)
      msgs.push(`${skippedExt.length} unsupported file(s) skipped (only JPG, PNG, WebP, HEIC).`);
    if (skippedSize.length)
      msgs.push(`${skippedSize.length} file(s) skipped (over ${RAW_ACCEPT_MB} MB raw).`);
    if (oversizeAfter.length)
      msgs.push(`${oversizeAfter.length} file(s) skipped (still over ${BACKEND_HARD_MB} MB after optimization).`);
    if (msgs.length) alert(msgs.join('\n'));
  };

  const removePreviewItem = (idx) => {
    setPreviewItems(prev => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleUpload = async () => {
    if (!previewItems.length || !consentChecked) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: previewItems.length });
    const results = [];

    for (let i = 0; i < previewItems.length; i++) {
      const formData = new FormData();
      formData.append('file', previewItems[i].file);
      formData.append('guest_name', guestName.trim());
      try {
        const res = await axios.post(`${BACKEND_URL}/api/public/galleries/${galleryId}/upload`, formData,
          { headers: { 'Content-Type': 'multipart/form-data' } });
        results.push({ name: previewItems[i].file.name, ok: true, status: res.data.status });
      } catch (e) {
        results.push({ name: previewItems[i].file.name, ok: false, error: e.response?.data?.detail || 'Upload failed' });
      }
      setUploadProgress({ done: i + 1, total: previewItems.length });
    }

    setUploadResults(results);
    previewItems.forEach(item => URL.revokeObjectURL(item.preview));
    setPreviewItems([]);
    setUploading(false);
    setConsentChecked(false);

    // Refresh
    try {
      const [galRes, photosRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/galleries/${galleryId}`),
        axios.get(`${BACKEND_URL}/api/public/galleries/${galleryId}/photos`).catch(() => ({ data: [] })),
      ]);
      setGallery(galRes.data);
      setPhotos(photosRes.data);
    } catch {}
  };

  /* ===== Render States ===== */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center" data-testid="gallery-loading">
        <GalleryDonateTab />
        <FloatingDonateTab />
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-[#BA1826] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#4B5563]">Loading event gallery...</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6" data-testid="gallery-not-found">
        <GalleryDonateTab />
        <FloatingDonateTab />
        <div className="text-center max-w-sm">
          <Camera className="w-10 h-10 text-[#BA1826] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-2">Gallery Not Found</h1>
          <p className="text-sm text-[#4B5563] mb-6">This event gallery doesn't exist or may have been removed.</p>
          <Link to="/" className="text-sm font-semibold text-[#BA1826] hover:underline">Visit Novena Humanitarian Foundation</Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6" data-testid="gallery-error">
        <GalleryDonateTab />
        <FloatingDonateTab />
        <div className="text-center"><h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-2">Something went wrong</h1>
          <button onClick={() => window.location.reload()} className="text-sm font-semibold text-[#BA1826] hover:underline">Refresh page</button>
        </div>
      </div>
    );
  }

  const isUploadsOpen = gallery.uploads_open && gallery.status === 'active';
  const isClosed = gallery.status === 'closed' || gallery.status === 'archived';
  const showPreview = previewItems.length > 0 && !uploadResults.length;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="gallery-upload-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] px-4 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="gallery-logo-link">
            <Heart className="w-5 h-5 text-[#BA1826] flex-shrink-0" fill="#BA1826" />
            <span className="font-heading font-bold text-[#0A0A0A] text-sm truncate">Novena Foundation</span>
          </Link>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="hidden sm:inline text-[10px] uppercase tracking-widest text-[#9CA3AF] font-semibold">Event Gallery</span>
            <a
              href="/donate"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 bg-[#BA1826] hover:bg-[#8B0F1C] text-white px-3 py-1.5 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-all"
              data-testid="gallery-header-donate-btn"
            >
              <Heart className="w-3 h-3" fill="currentColor" />
              Donate
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 pb-24 sm:pb-28">
        {/* Cover */}
        {gallery.cover_image && (
          <div className="rounded-xl overflow-hidden mb-5 shadow-sm" data-testid="gallery-cover-image">
            <img src={gallery.cover_image} alt={gallery.event_title} className="w-full h-48 object-cover" />
          </div>
        )}

        {/* Event Info */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 mb-5" data-testid="gallery-event-info">
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-3" data-testid="gallery-event-title">{gallery.event_title}</h1>
          <div className="flex flex-col gap-2 mb-3">
            {gallery.event_date && <div className="flex items-center gap-2 text-sm text-[#4B5563]"><Calendar className="w-4 h-4 text-[#BA1826]" /><span>{gallery.event_date}</span></div>}
            {gallery.event_location && <div className="flex items-center gap-2 text-sm text-[#4B5563]"><MapPin className="w-4 h-4 text-[#BA1826]" /><span>{gallery.event_location}</span></div>}
          </div>
          {gallery.event_description && <p className="text-sm text-[#4B5563] leading-relaxed">{gallery.event_description}</p>}
          {gallery.photo_count > 0 && (
            <p className="text-xs text-[#9CA3AF] mt-3 flex items-center gap-1">
              <ImageIcon className="w-3 h-3" /> {gallery.photo_count} photo{gallery.photo_count !== 1 ? 's' : ''} in the album
            </p>
          )}
        </div>

        {/* ===== Upload Section ===== */}
        {isUploadsOpen && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 mb-5" data-testid="gallery-upload-section">
            <h2 className="font-heading text-base font-bold text-[#0A0A0A] mb-1">Share Your Photos</h2>
            <p className="text-xs text-[#4B5563] mb-4">Take a photo or choose from your gallery to add to the event album.</p>

            {/* Hidden file inputs */}
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple
              className="hidden" onChange={handleFilesSelected} data-testid="file-input" />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
              className="hidden" onChange={handleFilesSelected} data-testid="camera-input" />

            {/* Step 1: Capture/Select */}
            {!showPreview && !uploading && !uploadResults.length && (
              <div className="space-y-3" data-testid="capture-options">
                <button onClick={() => cameraInputRef.current?.click()}
                  className="w-full py-4 bg-[#BA1826] text-white font-semibold text-sm rounded-xl hover:bg-[#A41623] transition-colors flex items-center justify-center gap-2.5"
                  data-testid="take-photo-btn">
                  <Aperture className="w-5 h-5" />
                  Take a Photo
                </button>
                <button onClick={() => fileInputRef.current?.click()}
                  className="w-full py-3.5 border-2 border-[#E5E7EB] text-[#0A0A0A] font-semibold text-sm rounded-xl hover:border-[#BA1826]/40 hover:bg-[#FEE2E2]/10 transition-colors flex items-center justify-center gap-2.5"
                  data-testid="choose-photo-btn">
                  <ImageIcon className="w-5 h-5 text-[#BA1826]" />
                  Choose from Gallery
                </button>
              </div>
            )}

            {/* Step 2: Preview + Consent + Upload/Save */}
            {showPreview && (
              <PhotoPreview
                items={previewItems}
                onUpload={handleUpload}
                onRemove={removePreviewItem}
                onAddMore={() => fileInputRef.current?.click()}
                guestName={guestName}
                setGuestName={setGuestName}
                consentChecked={consentChecked}
                setConsentChecked={setConsentChecked}
                uploading={uploading}
                uploadProgress={uploadProgress}
              />
            )}

            {/* Uploading Progress */}
            {uploading && !showPreview && (
              <div className="text-center py-8" data-testid="upload-progress">
                <Loader2 className="w-10 h-10 animate-spin text-[#BA1826] mx-auto mb-3" />
                <p className="text-sm font-semibold text-[#0A0A0A]">Uploading {uploadProgress.done} of {uploadProgress.total}...</p>
                <div className="w-full bg-[#E5E7EB] rounded-full h-2.5 mt-3">
                  <div className="bg-[#BA1826] h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }} />
                </div>
              </div>
            )}

            {/* Upload Results */}
            {!uploading && uploadResults.length > 0 && (
              <div data-testid="upload-results">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-3 text-center">
                  <CheckCircle className="w-7 h-7 text-green-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-green-800">
                    {uploadResults.filter(r => r.ok).length} photo{uploadResults.filter(r => r.ok).length !== 1 ? 's' : ''} submitted!
                  </p>
                  <p className="text-xs text-green-600 mt-1">Your photos are being reviewed and will appear in the album once approved.</p>
                </div>
                {uploadResults.some(r => !r.ok) && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                    {uploadResults.filter(r => !r.ok).map((r, i) => (
                      <p key={i} className="text-xs text-red-600">{r.name}: {r.error}</p>
                    ))}
                  </div>
                )}
                <button onClick={() => setUploadResults([])}
                  className="w-full py-2.5 border border-[#BA1826] text-[#BA1826] font-semibold text-sm rounded-lg hover:bg-[#FEE2E2]/30 transition-colors flex items-center justify-center gap-2"
                  data-testid="upload-more-btn">
                  <Camera className="w-4 h-4" /> Share More Photos
                </button>
              </div>
            )}

            {/* Consent Notice (always visible at bottom of upload section) */}
            <div className="mt-4 pt-3 border-t border-[#E5E7EB]" data-testid="consent-notice">
              <div className="flex items-start gap-2">
                <Shield className="w-3.5 h-3.5 text-[#9CA3AF] mt-0.5 flex-shrink-0" />
                <p className="text-[10px] text-[#9CA3AF] leading-relaxed">
                  <strong>Photo Consent & Usage:</strong> {CONSENT_TEXT}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Closed/Draft Messages */}
        {isClosed && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 text-center mb-5" data-testid="gallery-closed-section">
            <Clock className="w-8 h-8 text-[#4B5563] mx-auto mb-3" />
            <h2 className="font-heading text-lg font-bold text-[#0A0A0A] mb-2">Uploads Are Closed</h2>
            <p className="text-sm text-[#4B5563]">Photo uploads for this event have ended. Thank you for sharing your moments!</p>
          </div>
        )}
        {!isUploadsOpen && !isClosed && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 text-center mb-5" data-testid="gallery-draft-section">
            <Camera className="w-8 h-8 text-[#D97706] mx-auto mb-3" />
            <h2 className="font-heading text-lg font-bold text-[#0A0A0A] mb-2">Gallery Opening Soon</h2>
            <p className="text-sm text-[#4B5563]">Check back when the event starts to share your photos!</p>
          </div>
        )}

        {/* ===== Event Album (approved photos) ===== */}
        {photos.length > 0 && (
          <div className="mb-6" data-testid="event-album">
            <h3 className="font-heading text-base font-bold text-[#0A0A0A] mb-3 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-[#BA1826]" /> Event Album
            </h3>
            <PhotoGrid photos={photos} />
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 pt-6 border-t border-[#E5E7EB]">
          <p className="text-[11px] text-[#9CA3AF]">
            Powered by <Link to="/" className="font-semibold text-[#BA1826] hover:underline">Novena Humanitarian Foundation</Link>
          </p>
          <p className="text-[10px] text-[#D1D5DB] mt-1 italic">Transparency without exploitation. Support without shame.</p>
        </div>
      </main>

      {/* Persistent Donate Now CTA with live campaign progress */}
      <FloatingDonateTab />
    </div>
  );
}
